import { GraphQLBilletsRequester, type MultipleContractDto } from '@cobliteam/api-lib';
import type { Billet } from '@cobliteam/api-lib/src/__generated__/graphql';
import { type UseQueryOptions, useQueries, useQuery } from '@tanstack/react-query';

const QUERY_KEY = 'BILLETS';

type GetBilletsParams = {
  companyId: string;
  options?: Partial<UseQueryOptions<Billet[]>>;
};

export const calculateOverdueDays = (dueDate: string) => {
  const getStartOfDay = (date: Date) => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };

  const today = getStartOfDay(new Date());
  const due = getStartOfDay(new Date(dueDate));

  const diffTime = today.getTime() - due.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

const requester = new GraphQLBilletsRequester();

const useBillets = (params: GetBilletsParams) => {
  return useQuery<Billet[]>({
    ...params.options,
    queryKey: [QUERY_KEY, params.companyId],
    queryFn: () => requester.getBillets(params.companyId),
    enabled: !!params.companyId && (params.options?.enabled ?? true),
  });
};

const useOverdueBillets = (multipleContracts?: MultipleContractDto) => {
  const companyIds = multipleContracts?.data?.map((contract) => contract.companyId);

  return useQueries({
    queries:
      companyIds?.map((companyId) => ({
        queryKey: [QUERY_KEY, companyId],
        queryFn: () => requester.getBillets(companyId),
      })) ?? [],
    combine: (results) => {
      const mergedData: Billet[] = [];
      const isLoading = results.some((r) => r.isLoading);
      const isFetching = results.some((r) => r.isFetching);
      const isError = results.some((r) => r.isError);

      for (const result of results) {
        if (result.data) {
          mergedData.push(...result.data);
        }
      }

      const overdueBillets = mergedData.filter((billet) => {
        if (!['03', '07'].includes(billet.status || '')) return false;
        if (!billet.dataVencimento) return false;
        return calculateOverdueDays(billet.dataVencimento) > 0;
      });

      return { data: overdueBillets, isLoading, isFetching, isError };
    },
  });
};

export { useBillets, useOverdueBillets, QUERY_KEY };
export default useBillets;
